import React from 'react';
import { BrowserRouter, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Helmet } from 'react-helmet';
import { useEffect, useState } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'mdb-react-ui-kit/dist/css/mdb.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './services/Firebase';
import { auth } from './services/Firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './styles/global.css';
import { unregister } from './serviceWorker';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Packages from './pages/Packages';
import Contact from './pages/Contact';
import Sports from './pages/Sports';
import Login from './pages/Login';
import Settings from './pages/Settings';
import SystemDocs from './pages/SystemDocs';
import PropsPage from './pages/Props';
import { BRANDING } from './config/branding';
import { isAppRoute } from './utils/constants';
import { getUserAccess, getUserProfile } from './api/user';
import { resolveLandingSport } from './services/Firebase/authHelper';
import LoadingSpinner from './components/LoadingSpinner';
import { setAccess, clearAccess } from './store/slices/accessSlice';
import { setProfile, clearUserState } from './store/slices/userSlice';
import store from './store';

const theme = {
    colors: {
        primary: '#006eb5',
        primaryDark: '#1c2237',
        primaryMedium: '#9be4ff',
        primaryLight: '#cdf1ff',
        darkGray: '#7a7a7a',
        gray: '#D3D3D3',
        white: '#fff',
        black: '#111',
        red: '#d63031',
        green: '#00b894',
        gold: '#bf9b30',
        silver: '#f5f7fb',
        borderColor: '#ddd',
        textSecondary: '#6c757d',
        lightGray: '#f8f9fa',
    },
    fonts: {
        heading: 'Poppins, sans-serif',
        body: 'Roboto, sans-serif',
    },
};

const GlobalStyle = createGlobalStyle`
    * { box-sizing: border-box; }
    body { font-family: ${theme.fonts.body}; }
    span { font-size: 12px; }
`;

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

const SubscriptionGate = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [authReady, setAuthReady] = useState(false);
  const [checking, setChecking] = useState(true);
  // Track whether the very first access-check has completed so subsequent
  // sport-to-sport navigations don't replace the whole page with a spinner.
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthReady(true);
    });

    return unsubscribe;
  }, []);

  // Load the profile once per auth change (not per navigation like the access check).
  useEffect(() => {
    if (!authReady) {
      return;
    }

    if (!currentUser) {
      dispatch(clearUserState());
      return;
    }

    const loadProfile = async () => {
      try {
        const profile = await getUserProfile();
        dispatch(setProfile(profile));
      } catch {
        dispatch(setProfile(null));
      }
    };

    loadProfile();
  }, [authReady, currentUser]);

  useEffect(() => {
    if (!authReady) return;

    const checkAccess = async () => {
      setChecking(true);
      try {
        const path = location.pathname;
        const isLoginPage = path.startsWith('/login');
        const protectedRoute = isAppRoute(path);

        if (!currentUser) {
          if (protectedRoute) {
            history.replace('/login');
          }
          dispatch(clearAccess());
          return;
        }

        const access = await getUserAccess();
        dispatch(setAccess(access));
        const hasPackage = Boolean(access?.paid);

        if (isLoginPage) {
          history.replace(
            hasPackage
              ? `/sports/${resolveLandingSport(access)}`
              : '/settings?tab=subscription-management'
          );
          return;
        }

        if (!hasPackage) {
          if (path.startsWith('/profile')) {
            history.replace('/settings?tab=profile');
            return;
          }
        }

      } catch {
        if (isAppRoute(location.pathname) && !location.pathname.startsWith('/login')) {
          history.replace('/login');
        }
      } finally {
        setChecking(false);
        setInitialCheckDone(true);
      }
    };

    checkAccess();
  }, [authReady, currentUser, history, location.pathname]);

  // Only block rendering on the very first auth+access check.
  // Subsequent sport-to-sport navigations run the check silently in the
  // background so the Header (and app shell) never disappears.
  if (!authReady || (checking && !initialCheckDone)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  return children;
};

const App = () => (
  <BrowserRouter>
    <ScrollToTop />
    <SubscriptionGate>
      <Helmet>
        <title>{BRANDING.seo.defaultTitle}</title>
        <meta name="description" content={BRANDING.seo.defaultDescription} />
        <meta property="og:title" content={BRANDING.seo.defaultTitle} />
        <meta property="og:description" content={BRANDING.seo.defaultDescription} />
        <meta property="og:image" content={BRANDING.seo.ogImage} />
      </Helmet>
      <Header />
      <Switch>
        <Route exact path="/" component={Home} />
        <Route exact path="/packages" component={Packages} />
        <Route exact path="/contact" component={Contact} />
        <Route exact path="/system-docs" component={SystemDocs} />
        <Route exact path="/login" component={Login} />
        <Route exact path="/settings" component={Settings} />
        <Route exact path="/profile" component={Settings} />
        <Route exact path="/subscription-management" component={Settings} />
        <Route exact path="/props" component={PropsPage} />
        <Route path="/props/:sport" component={PropsPage} />
        <Route path="/sports/:sport" component={Sports} />
      </Switch>
      <Footer />
    </SubscriptionGate>
  </BrowserRouter>
);

const container = document.getElementById('root');
const app = (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <App />
    </ThemeProvider>
  </Provider>
);
if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}

const isPrerender = navigator.webdriver || /HeadlessChrome/i.test(navigator.userAgent);
if (!isPrerender) {
  const removeSplash = () => {
    const splash = document.getElementById('pwp-splash');
    if (!splash) return;
    splash.classList.add('pwp-fade');
    setTimeout(() => splash.remove(), 450);
  };
  const maxWait = setTimeout(removeSplash, 1500);
  document.fonts.ready.then(() => {
    clearTimeout(maxWait);
    removeSplash();
  });
}

unregister();
